import {
  BarChartOutlined,
  BoltOutlined,
  CancelOutlined,
  CheckCircleOutlined,
  ConfirmationNumberOutlined,
  FlashOnOutlined,
  ForumOutlined,
  GroupOutlined,
} from "@mui/icons-material";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  GlobalStyles,
  Grid,
  Link,
  Paper,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import React, { useContext } from "react";
import { Link as RouterLink } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";

// ── Brand colours (not theme-dependent — identity stays green in dark mode) ──
const GREEN = "#1A6E2E";
const TEAL = "#0F7B6C";
const GREEN2 = "#25A244";
const SURFACE = "#E8F5EC";

// ── Static data ────────────────────────────────────────────────────────────────
const STATS = [
  { value: "21,000+", label: "Messages / day" },
  { value: "99.9%", label: "Uptime" },
  { value: "< 1s", label: "Delivery time" },
];

const FEATURES = [
  {
    Icon: ForumOutlined,
    title: "WhatsApp Inbox",
    desc: "Unified inbox for all customer WhatsApp conversations in one place.",
  },
  {
    Icon: GroupOutlined,
    title: "Multi-Agent",
    desc: "Assign tickets to agents and collaborate in real time across your team.",
  },
  {
    Icon: ConfirmationNumberOutlined,
    title: "Ticket Management",
    desc: "Create, track and resolve support tickets with full history.",
  },
  {
    Icon: BoltOutlined,
    title: "Real-Time Updates",
    desc: "Live ticket status and message updates with zero page refresh.",
  },
  {
    Icon: FlashOnOutlined,
    title: "Quick Replies",
    desc: "Pre-built templates to answer common questions instantly.",
  },
  {
    Icon: BarChartOutlined,
    title: "Analytics",
    desc: "Track team performance, response times and ticket volume.",
  },
];

const STEPS = [
  {
    n: 1,
    title: "Connect WhatsApp",
    desc: "Scan a QR code to link your WhatsApp number in seconds.",
  },
  {
    n: 2,
    title: "Assign to Agents",
    desc: "Incoming messages auto-create tickets routed to the right queue.",
  },
  {
    n: 3,
    title: "Resolve & Delight",
    desc: "Agents reply, close tickets and customers feel heard — fast.",
  },
];

const MOCK_TICKETS = [
  {
    initials: "JD",
    name: "John Doe",
    preview: "Hey, my order hasn't arrived yet...",
    status: "open",
  },
  {
    initials: "SA",
    name: "Sarah A.",
    preview: "Can I change my delivery address?",
    status: "pending",
  },
  {
    initials: "MK",
    name: "Mike K.",
    preview: "Thank you for the quick response!",
    status: "closed",
  },
];
const STATUS_COLOR = { open: "#25A244", pending: "#D4821A", closed: "#7A9A7E" };
const STATUS_BG = { open: "#E8F5EC", pending: "#FDF3E3", closed: "#F0F0F0" };

const PLAN_NOTE =
  "First connected WhatsApp number is free. Additional connections billed per plan.";

const PLANS = [
  {
    name: "Starter",
    price: "$19",
    period: "/ user / mo",
    popular: false,
    cta: "Start Free Trial",
    ctaVariant: "outlined",
    href: "/signup",
    groups: [
      {
        title: "Inbox & Agents",
        items: [
          { label: "1 WhatsApp connection included", included: true },
          { label: "Multi-agent team access", included: true },
          { label: "Contact management", included: true },
        ],
      },
      {
        title: "Tickets & Queues",
        items: [
          { label: "Ticket management", included: true },
          { label: "Queue routing", included: true },
          { label: "Quick replies", included: true },
        ],
      },
      {
        title: "Analytics",
        items: [{ label: "Basic ticket metrics", included: true }],
      },
    ],
  },
  {
    name: "Pro",
    price: "$30",
    period: "/ user / mo",
    popular: true,
    cta: "Start Free Trial",
    ctaVariant: "contained",
    href: "/signup",
    groups: [
      {
        title: "Everything in Starter, plus",
        items: [
          { label: "Advanced analytics", included: true },
          { label: "Contact masking for privacy", included: true },
          { label: "Automated ticket rules", included: true },
        ],
      },
    ],
  },
  {
    name: "Enterprise",
    price: "$39",
    period: "/ user / mo",
    popular: false,
    cta: "Start Free Trial",
    ctaVariant: "outlined",
    href: "/signup",
    groups: [
      {
        title: "Everything in Pro, plus",
        items: [
          { label: "Minimum 50 users or phones", included: true },
          { label: "Custom billing & invoicing", included: true },
          { label: "Dedicated SLA & onboarding", included: true },
          { label: "24/7 priority support", included: true },
        ],
      },
    ],
  },
];

// ── Shared section header ──────────────────────────────────────────────────────
const SectionHeader = ({ eyebrow, title, subtitle }) => (
  <Box sx={{ textAlign: "center", mb: 8 }}>
    <Chip
      label={eyebrow}
      size="small"
      sx={{ mb: 2, fontWeight: 600, bgcolor: SURFACE, color: GREEN }}
    />
    <Typography
      variant="h3"
      fontWeight={800}
      letterSpacing="-0.5px"
      sx={{ mb: 2 }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ maxWidth: 520, mx: "auto", lineHeight: 1.7 }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

// ── 1. Navbar ──────────────────────────────────────────────────────────────────
const LandingNav = ({ isAuth }) => (
  <AppBar
    position="sticky"
    elevation={0}
    sx={{
      bgcolor: "background.paper",
      borderBottom: "1px solid",
      borderColor: "divider",
    }}
  >
    <Toolbar
      sx={{ maxWidth: "lg", mx: "auto", width: "100%", px: { xs: 2, md: 3 } }}
    >
      {/* Brand */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ flexShrink: 0 }}
      >
        <Box
          sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: GREEN }}
        />
        <Typography
          fontWeight={800}
          fontSize={17}
          color={GREEN}
          letterSpacing="-0.3px"
        >
          Hashlix CRM
        </Typography>
      </Stack>

      {/* Nav links — desktop only */}
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          display: { xs: "none", md: "flex" },
          flex: 1,
          justifyContent: "center",
        }}
      >
        {[
          { label: "Features", href: "#features" },
          { label: "How it works", href: "#how-it-works" },
          { label: "Pricing", href: "#pricing" },
        ].map(({ label, href }) => (
          <Button
            key={label}
            component="a"
            href={href}
            color="inherit"
            sx={{
              textTransform: "none",
              fontWeight: 500,
              color: "text.secondary",
              fontSize: 14,
              "&:hover": { color: GREEN, bgcolor: "transparent" },
            }}
          >
            {label}
          </Button>
        ))}
      </Stack>

      {/* CTAs */}
      <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
        {isAuth ? (
          <Button
            component={RouterLink}
            to="/dashboard"
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              bgcolor: GREEN,
              "&:hover": { bgcolor: GREEN2 },
            }}
          >
            Go to Dashboard
          </Button>
        ) : (
          <>
            <Button
              component={RouterLink}
              to="/login"
              color="inherit"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "text.primary",
                display: { xs: "none", sm: "inline-flex" },
              }}
            >
              Sign in
            </Button>
            <Button
              component={RouterLink}
              to="/signup"
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                bgcolor: GREEN,
                "&:hover": { bgcolor: GREEN2 },
              }}
            >
              Get Started
            </Button>
          </>
        )}
      </Stack>
    </Toolbar>
  </AppBar>
);

// ── 2. Hero ────────────────────────────────────────────────────────────────────
const HeroSection = ({ isAuth }) => (
  <Box
    sx={{
      background: `linear-gradient(135deg, ${GREEN} 0%, ${TEAL} 100%)`,
      color: "#fff",
      py: { xs: 10, md: 14 },
      overflow: "hidden",
      position: "relative",
    }}
  >
    {/* Decorative circles */}
    {[
      { size: 420, top: -120, right: -80 },
      { size: 260, bottom: -60, left: "10%" },
    ].map(({ size, ...pos }, i) => (
      <Box
        key={i}
        sx={{
          position: "absolute",
          width: size,
          height: size,
          borderRadius: "50%",
          bgcolor: "rgba(255,255,255,0.06)",
          pointerEvents: "none",
          ...pos,
        }}
      />
    ))}

    <Container maxWidth="lg" sx={{ position: "relative" }}>
      <Grid container spacing={6} alignItems="center">
        {/* Left — copy */}
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ animation: "fadeInUp 0.6s ease-out both" }}
        >
          <Chip
            label="WhatsApp-Native CRM"
            size="small"
            sx={{
              mb: 3,
              fontWeight: 700,
              bgcolor: "rgba(255,255,255,0.15)",
              color: "#fff",
            }}
          />
          <Typography
            variant="h2"
            fontWeight={800}
            letterSpacing="-1px"
            lineHeight={1.12}
            sx={{ mb: 3 }}
          >
            Turn WhatsApp into your team's support HQ
          </Typography>
          <Typography
            variant="h6"
            sx={{ opacity: 0.85, fontWeight: 400, lineHeight: 1.65, mb: 4 }}
          >
            Manage tickets, collaborate with agents and delight customers — all
            inside WhatsApp.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            {isAuth ? (
              <Button
                component={RouterLink}
                to="/dashboard"
                variant="contained"
                size="large"
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 2.5,
                  px: 4,
                  bgcolor: "#fff",
                  color: GREEN,
                  "&:hover": { bgcolor: "rgba(255,255,255,0.92)" },
                }}
              >
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Button
                  component={RouterLink}
                  to="/signup"
                  variant="contained"
                  size="large"
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2.5,
                    px: 4,
                    bgcolor: "#fff",
                    color: GREEN,
                    "&:hover": { bgcolor: "rgba(255,255,255,0.92)" },
                  }}
                >
                  Get Started Free
                </Button>
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="outlined"
                  size="large"
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2.5,
                    px: 4,
                    borderColor: "rgba(255,255,255,0.6)",
                    color: "#fff",
                    "&:hover": {
                      borderColor: "#fff",
                      bgcolor: "rgba(255,255,255,0.08)",
                    },
                  }}
                >
                  Sign In
                </Button>
              </>
            )}
          </Stack>
        </Grid>

        {/* Right — mock product screenshot */}
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ animation: "fadeInUp 0.6s ease-out 0.2s both" }}
        >
          <Box sx={{ perspective: "1000px" }}>
            <Paper
              elevation={16}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                transform: "rotateY(-4deg) rotateX(2deg)",
                transition: "transform 0.45s ease",
                "&:hover": { transform: "rotateY(0) rotateX(0)" },
              }}
            >
              {/* Window bar */}
              <Box
                sx={{
                  bgcolor: GREEN,
                  px: 2,
                  py: 1.25,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {["#FF5F57", "#FEBC2E", "#28C840"].map((c) => (
                  <Box
                    key={c}
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: c,
                    }}
                  />
                ))}
                <Typography
                  variant="caption"
                  sx={{
                    ml: 1,
                    color: "rgba(255,255,255,0.8)",
                    fontWeight: 600,
                  }}
                >
                  Hashlix CRM — Tickets
                </Typography>
              </Box>

              {/* Fake tickets */}
              <Box sx={{ bgcolor: "#F5F7F5", p: 2 }}>
                {MOCK_TICKETS.map(({ initials, name, preview, status }) => (
                  <Paper
                    key={name}
                    elevation={0}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      mb: 1,
                      borderRadius: 2,
                      border: "1px solid #D4E0D5",
                      bgcolor: "#fff",
                      "&:last-child": { mb: 0 },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 34,
                        height: 34,
                        bgcolor: SURFACE,
                        color: GREEN,
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {initials}
                    </Avatar>
                    <Box flex={1} minWidth={0}>
                      <Typography
                        component="div"
                        variant="caption"
                        fontWeight={700}
                        noWrap
                        sx={{ color: "#1A2E1C" }}
                      >
                        {name}
                      </Typography>
                      <Typography
                        component="div"
                        variant="caption"
                        noWrap
                        sx={{ color: "#4A6E4F" }}
                      >
                        {preview}
                      </Typography>
                    </Box>
                    <Chip
                      label={status}
                      size="small"
                      sx={{
                        fontSize: 10,
                        height: 20,
                        fontWeight: 700,
                        flexShrink: 0,
                        bgcolor: STATUS_BG[status],
                        color: STATUS_COLOR[status],
                        "& .MuiChip-label": { color: STATUS_COLOR[status] },
                      }}
                    />
                  </Paper>
                ))}
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

// ── 3. Stats bar ───────────────────────────────────────────────────────────────
const StatsBar = () => (
  <Box
    sx={{
      bgcolor: "background.paper",
      borderTop: "1px solid",
      borderBottom: "1px solid",
      borderColor: "divider",
      py: { xs: 5, md: 6 },
    }}
  >
    <Container maxWidth="md">
      <Grid container alignItems="center" justifyContent="center">
        {STATS.map(({ value, label }, i) => (
          <React.Fragment key={label}>
            {i > 0 && (
              <Grid
                size={{ xs: 12, sm: "auto" }}
                sx={{ display: { xs: "none", sm: "block" } }}
              >
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ height: 48, mx: 4 }}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: "auto" }} sx={{ textAlign: "center" }}>
              <Typography
                variant="h3"
                fontWeight={800}
                color={GREEN}
                letterSpacing="-1px"
              >
                {value}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
                sx={{ mt: 0.5 }}
              >
                {label}
              </Typography>
            </Grid>
          </React.Fragment>
        ))}
      </Grid>
    </Container>
  </Box>
);

// ── 4. Features ────────────────────────────────────────────────────────────────
const FeaturesSection = () => (
  <Box
    component="section"
    id="features"
    sx={{ bgcolor: "background.default", py: { xs: 10, md: 12 } }}
  >
    <Container maxWidth="lg">
      <SectionHeader
        eyebrow="Features"
        title="Everything your team needs"
        subtitle="A complete WhatsApp support platform built for speed, collaboration and scale."
      />
      <Grid container spacing={3}>
        {FEATURES.map(({ Icon, title, desc }, i) => (
          <Grid key={title} size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3.5,
                height: "100%",
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                transition: "box-shadow 0.2s, transform 0.2s",
                "&:hover": {
                  boxShadow: "0 8px 32px rgba(26,110,46,0.10)",
                  transform: "translateY(-2px)",
                },
                animation: "fadeInUp 0.55s ease-out both",
                animationDelay: `${0.05 * i}s`,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: SURFACE,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <Icon sx={{ fontSize: 26, color: GREEN }} />
              </Box>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                {title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                lineHeight={1.7}
              >
                {desc}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

// ── 5. How it works ────────────────────────────────────────────────────────────
const HowItWorksSection = () => (
  <Box
    component="section"
    id="how-it-works"
    sx={{ bgcolor: "background.paper", py: { xs: 10, md: 12 } }}
  >
    <Container maxWidth="lg">
      <SectionHeader
        eyebrow="How it works"
        title="Up and running in minutes"
        subtitle="No complex setup. Just connect, configure and start resolving tickets."
      />
      <Grid container spacing={{ xs: 6, md: 0 }} alignItems="flex-start">
        {STEPS.map(({ n, title, desc }, i) => (
          <Grid
            key={n}
            size={{ xs: 12, md: 4 }}
            sx={{ position: "relative", textAlign: "center" }}
          >
            {/* Connector line */}
            {i < STEPS.length - 1 && (
              <Box
                sx={{
                  display: { xs: "none", md: "block" },
                  position: "absolute",
                  top: 27,
                  left: "calc(50% + 28px)",
                  right: "calc(-50% + 28px)",
                  height: 2,
                  bgcolor: "divider",
                }}
              />
            )}
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                bgcolor: GREEN,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 22,
                fontWeight: 800,
                mx: "auto",
                mb: 3,
              }}
            >
              {n}
            </Box>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              lineHeight={1.7}
              sx={{ maxWidth: 260, mx: "auto" }}
            >
              {desc}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

// ── 6. Pricing ─────────────────────────────────────────────────────────────────
const PricingSection = () => (
  <Box
    component="section"
    id="pricing"
    sx={{ bgcolor: "background.default", py: { xs: 10, md: 12 } }}
  >
    <Container maxWidth="lg">
      <SectionHeader
        eyebrow="Simple Pricing"
        title="Plans that scale with your team"
        subtitle="One included WhatsApp number per plan. Additional connections billed separately."
      />

      <Grid container spacing={3} alignItems="stretch">
        {PLANS.map((plan) => (
          <Grid key={plan.name} size={{ xs: 12, sm: 6, md: 4 }}>
            <Paper
              elevation={plan.popular ? 4 : 0}
              sx={{
                p: 4,
                height: "100%",
                borderRadius: 3,
                position: "relative",
                display: "flex",
                flexDirection: "column",
                border: plan.popular ? `2px solid ${GREEN}` : "1px solid",
                borderColor: plan.popular ? GREEN : "divider",
                bgcolor: "background.paper",
                transition: "box-shadow 0.2s, transform 0.2s",
                "&:hover": {
                  boxShadow: "0 8px 32px rgba(26,110,46,0.12)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              {plan.popular && (
                <Chip
                  label="Most Popular"
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    fontWeight: 700,
                    fontSize: 11,
                    bgcolor: GREEN,
                    color: "#fff",
                  }}
                />
              )}

              {/* Plan name */}
              <Typography
                variant="overline"
                fontWeight={700}
                color={GREEN}
                letterSpacing="0.1em"
              >
                {plan.name}
              </Typography>

              {/* Price */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 0.75,
                  mt: 1,
                  mb: 0.5,
                }}
              >
                <Typography variant="h3" fontWeight={800} letterSpacing="-1px">
                  {plan.price}
                </Typography>
                {plan.price !== "Custom" && (
                  <Typography variant="body2" color="text.secondary">
                    {plan.period}
                  </Typography>
                )}
              </Box>
              {plan.price === "Custom" && (
                <Typography variant="body2" color="text.secondary">
                  {plan.period}
                </Typography>
              )}

              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ mb: 2.5, lineHeight: 1.5 }}
              >
                {PLAN_NOTE}
              </Typography>

              {/* CTA */}
              <Button
                component={RouterLink}
                to={plan.href}
                fullWidth
                variant={plan.ctaVariant}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 2,
                  py: 1.25,
                  mb: 3,
                  ...(plan.ctaVariant === "contained"
                    ? { bgcolor: GREEN, "&:hover": { bgcolor: GREEN2 } }
                    : {
                        borderColor: GREEN,
                        color: GREEN,
                        "&:hover": { bgcolor: SURFACE },
                      }),
                }}
              >
                {plan.cta}
              </Button>

              <Divider />

              {/* Feature groups */}
              <Box sx={{ mt: 2.5, flex: 1 }}>
                {plan.groups.map((group) => (
                  <Box key={group.title} sx={{ mb: 2.5 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ mb: 1.25 }}
                    >
                      {group.title}
                    </Typography>
                    <Stack spacing={1}>
                      {group.items.map(({ label, included }) => (
                        <Stack
                          key={label}
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                        >
                          {included ? (
                            <CheckCircleOutlined
                              sx={{
                                fontSize: 17,
                                color: GREEN,
                                flexShrink: 0,
                                mt: "1px",
                              }}
                            />
                          ) : (
                            <CancelOutlined
                              sx={{
                                fontSize: 17,
                                color: "text.disabled",
                                flexShrink: 0,
                                mt: "1px",
                              }}
                            />
                          )}
                          <Typography
                            variant="body2"
                            color={included ? "text.primary" : "text.disabled"}
                            lineHeight={1.55}
                          >
                            {label}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

// ── 7. CTA banner ──────────────────────────────────────────────────────────────
const CtaBanner = ({ isAuth }) => (
  <Box
    sx={{
      background: `linear-gradient(135deg, ${GREEN} 0%, ${TEAL} 100%)`,
      py: { xs: 10, md: 12 },
      color: "#fff",
      textAlign: "center",
    }}
  >
    <Container maxWidth="sm">
      <Typography
        variant="h3"
        fontWeight={800}
        letterSpacing="-0.5px"
        sx={{ mb: 2 }}
      >
        Ready to transform your support?
      </Typography>
      <Typography
        variant="body1"
        sx={{ opacity: 0.85, mb: 5, lineHeight: 1.7 }}
      >
        Join thousands of teams resolving customer queries faster with Hashlix
        CRM.
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        justifyContent="center"
      >
        {isAuth ? (
          <Button
            component={RouterLink}
            to="/dashboard"
            variant="contained"
            size="large"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2.5,
              px: 4,
              bgcolor: "#fff",
              color: GREEN,
              "&:hover": { bgcolor: "rgba(255,255,255,0.92)" },
            }}
          >
            Go to Dashboard
          </Button>
        ) : (
          <>
            <Button
              component={RouterLink}
              to="/signup"
              variant="contained"
              size="large"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2.5,
                px: 4,
                bgcolor: "#fff",
                color: GREEN,
                "&:hover": { bgcolor: "rgba(255,255,255,0.92)" },
              }}
            >
              Get Started Free
            </Button>
            <Button
              component={RouterLink}
              to="/login"
              variant="outlined"
              size="large"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2.5,
                px: 4,
                borderColor: "rgba(255,255,255,0.6)",
                color: "#fff",
                "&:hover": {
                  borderColor: "#fff",
                  bgcolor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              Sign In
            </Button>
          </>
        )}
      </Stack>
    </Container>
  </Box>
);

// ── 8. Footer ──────────────────────────────────────────────────────────────────
const LandingFooter = () => (
  <Box
    component="footer"
    sx={{
      bgcolor: "background.paper",
      borderTop: "1px solid",
      borderColor: "divider",
      py: 4,
    }}
  >
    <Container maxWidth="lg">
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        {/* Brand */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: GREEN }}
          />
          <Typography fontWeight={700} fontSize={14} color={GREEN}>
            Hashlix CRM
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
            WhatsApp-native CRM for modern teams
          </Typography>
        </Stack>

        {/* Links */}
        <Stack direction="row" spacing={3} alignItems="center">
          {["Privacy", "Terms"].map((label) => (
            <Link
              key={label}
              href="#"
              underline="hover"
              variant="body2"
              color="text.secondary"
              sx={{ "&:hover": { color: GREEN } }}
            >
              {label}
            </Link>
          ))}
          <Typography variant="caption" color="text.disabled">
            © {new Date().getFullYear()} Hashlix CRM
          </Typography>
        </Stack>
      </Stack>
    </Container>
  </Box>
);

// ── Root component ─────────────────────────────────────────────────────────────
const Landing = () => {
  const { isAuth } = useContext(AuthContext);

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      <GlobalStyles
        styles={{
          "@keyframes fadeInUp": {
            from: { opacity: 0, transform: "translateY(28px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          html: { scrollBehavior: "smooth" },
        }}
      />
      <LandingNav isAuth={isAuth} />
      <HeroSection isAuth={isAuth} />
      <StatsBar />
      <FeaturesSection />
      <HowItWorksSection />
      <PricingSection />
      <CtaBanner isAuth={isAuth} />
      <LandingFooter />
    </Box>
  );
};

export default Landing;
